import { PaginationDropdownLimit } from '@mesa-labs/mesa-ui/dist/components/Pagination';
import { types as api } from '@mesa-labs/mesa-api';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type VendorAccountUnderwritingState = {
  overviewByVendorAccountIdBeginningAt?: Record<string, api.ISODateString | undefined>;
  overviewByVendorAccountIdEndingAt?: Record<string, api.ISODateString | undefined>;
  overviewByVendorAccountIdPeriodization?: Record<string, api.TimeSeriesPeriodization | undefined>;
  overviewByVendorAccountIdMinimumBalance?: Record<string, number | undefined>;
  transactionsAmountLow?: number;
  transactionsAmountHigh?: number;
  transactionsMerchantName?: string;
  transactionsCategory?: string;
  transactionsAuthorizedBeginningAt?: api.ISODateString;
  transactionsAuthorizedEndingAt?: api.ISODateString;
  transactionsPostedBeginningAt?: api.ISODateString;
  transactionsPostedEndingAt?: api.ISODateString;
  matchingInvoicesClientName?: string;
  matchingInvoicesBeginningAt?: api.ISODateString;
  matchingInvoicesEndingAt?: api.ISODateString;
  matchingInvoicesPage?: number;
  matchingInvoicesTotalPages?: number;
  matchingInvoicesLimit?: PaginationDropdownLimit;
  matchingInvoicesSortField?: string;
  matchingInvoicesSortDirection: api.SortDirection;
  matchingExternalInvoices?: boolean;
  matchingExternalInvoicesClientName?: string;
  matchingExternalInvoicesBeginningAt?: api.ISODateString;
  matchingExternalInvoicesEndingAt?: api.ISODateString;
  matchingExternalInvoicesPage?: number;
  matchingExternalInvoicesTotalPages?: number;
  matchingExternalInvoicesLimit?: PaginationDropdownLimit;
  matchingExternalInvoicesSortField?: string;
  matchingExternalInvoicesSortDirection: api.SortDirection;
  matchingPaymentsMerchantName?: string;
  matchingPaymentsBeginningAt?: api.ISODateString;
  matchingPaymentsEndingAt?: api.ISODateString;
  matchingPaymentsPage?: number;
  matchingPaymentsTotalPages?: number;
  matchingPaymentsLimit?: PaginationDropdownLimit;
  matchingPaymentsSortField?: string;
  matchingPaymentsSortDirection: api.SortDirection;
};

export const initialState: VendorAccountUnderwritingState = {
  overviewByVendorAccountIdBeginningAt: undefined,
  overviewByVendorAccountIdEndingAt: undefined,
  overviewByVendorAccountIdPeriodization: undefined,
  overviewByVendorAccountIdMinimumBalance: undefined,
  transactionsAmountLow: undefined,
  transactionsAmountHigh: undefined,
  transactionsMerchantName: undefined,
  transactionsCategory: undefined,
  transactionsAuthorizedBeginningAt: undefined,
  transactionsAuthorizedEndingAt: undefined,
  transactionsPostedBeginningAt: undefined,
  transactionsPostedEndingAt: undefined,
  matchingInvoicesClientName: undefined,
  matchingInvoicesBeginningAt: undefined,
  matchingInvoicesEndingAt: undefined,
  matchingInvoicesPage: 1,
  matchingInvoicesTotalPages: undefined,
  matchingInvoicesLimit: { key: '10', value: 10 },
  matchingInvoicesSortField: undefined,
  matchingInvoicesSortDirection: api.SortDirection.ASCENDING,
  matchingExternalInvoices: false,
  matchingExternalInvoicesClientName: undefined,
  matchingExternalInvoicesBeginningAt: undefined,
  matchingExternalInvoicesEndingAt: undefined,
  matchingExternalInvoicesPage: 1,
  matchingExternalInvoicesTotalPages: undefined,
  matchingExternalInvoicesLimit: { key: '10', value: 10 },
  matchingExternalInvoicesSortField: undefined,
  matchingExternalInvoicesSortDirection: api.SortDirection.ASCENDING,
  matchingPaymentsMerchantName: undefined,
  matchingPaymentsBeginningAt: undefined,
  matchingPaymentsEndingAt: undefined,
  matchingPaymentsPage: 1,
  matchingPaymentsTotalPages: undefined,
  matchingPaymentsLimit: { key: '10', value: 10 },
  matchingPaymentsSortField: undefined,
  matchingPaymentsSortDirection: api.SortDirection.ASCENDING,
};

const vendorAccountUnderwritingSlice = createSlice({
  name: 'vendorAccountUnderwriting',
  initialState,
  reducers: {
    updateOverviewByVendorAccountIdBeginningAt(state: VendorAccountUnderwritingState, action: PayloadAction<{ vendorAccountId: string | undefined; overviewBeginningAt: api.ISODateString | undefined }>) {
      if (!action.payload.vendorAccountId) {
        return;
      }
      state.overviewByVendorAccountIdBeginningAt = state.overviewByVendorAccountIdBeginningAt || {};
      state.overviewByVendorAccountIdBeginningAt[action.payload.vendorAccountId] = action.payload.overviewBeginningAt;
    },
    updateOverviewByVendorAccountIdEndingAt(state: VendorAccountUnderwritingState, action: PayloadAction<{ vendorAccountId: string | undefined; overviewEndingAt: api.ISODateString | undefined }>) {
      if (!action.payload.vendorAccountId) {
        return;
      }
      state.overviewByVendorAccountIdEndingAt = state.overviewByVendorAccountIdEndingAt || {};
      state.overviewByVendorAccountIdEndingAt[action.payload.vendorAccountId] = action.payload.overviewEndingAt;
    },
    updateOverviewByVendorAccountIdPeriodization(state: VendorAccountUnderwritingState, action: PayloadAction<{ vendorAccountId: string | undefined; overviewPeriodization: api.TimeSeriesPeriodization | undefined }>) {
      if (!action.payload.vendorAccountId) {
        return;
      }
      state.overviewByVendorAccountIdPeriodization = state.overviewByVendorAccountIdPeriodization || {};
      state.overviewByVendorAccountIdPeriodization[action.payload.vendorAccountId] = action.payload.overviewPeriodization;
    },
    updateOverviewByVendorAccountIdMinimumBalance(state: VendorAccountUnderwritingState, action: PayloadAction<{ vendorAccountId: string | undefined; overviewMinimumBalance: number | undefined }>) {
      if (!action.payload.vendorAccountId) {
        return;
      }
      state.overviewByVendorAccountIdMinimumBalance = state.overviewByVendorAccountIdMinimumBalance || {};
      state.overviewByVendorAccountIdMinimumBalance[action.payload.vendorAccountId] = action.payload.overviewMinimumBalance;
    },
    updateTransactionsAmountLow(state: VendorAccountUnderwritingState, action: PayloadAction<number | undefined>) {
      state.transactionsAmountLow = action.payload;
    },
    updateTransactionsAmountHigh(state: VendorAccountUnderwritingState, action: PayloadAction<number | undefined>) {
      state.transactionsAmountHigh = action.payload;
    },
    updateTransactionsMerchantName(state: VendorAccountUnderwritingState, action: PayloadAction<string | undefined>) {
      state.transactionsMerchantName = action.payload;
    },
    updateTransactionsCategory(state: VendorAccountUnderwritingState, action: PayloadAction<string | undefined>) {
      state.transactionsCategory = action.payload;
    },
    updateTransactionsAuthorizedBeginningAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.transactionsAuthorizedBeginningAt = action.payload;
    },
    updateTransactionsAuthorizedEndingAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.transactionsAuthorizedEndingAt = action.payload;
    },
    updateTransactionsPostedBeginningAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.transactionsPostedBeginningAt = action.payload;
    },
    updateTransactionsPostedEndingAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.transactionsPostedEndingAt = action.payload;
    },
    updateMatchingInvoicesClientName(state: VendorAccountUnderwritingState, action: PayloadAction<string | undefined>) {
      state.matchingInvoicesClientName = action.payload;
    },
    updateMatchingInvoicesBeginningAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.matchingInvoicesBeginningAt = action.payload;
    },
    updateMatchingInvoicesEndingAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.matchingInvoicesEndingAt = action.payload;
    },
    updateMatchingInvoicesPage(state: VendorAccountUnderwritingState, action: PayloadAction<number>) {
      state.matchingInvoicesPage = action.payload;
    },
    updateMatchingInvoicesTotalPages(state: VendorAccountUnderwritingState, action: PayloadAction<number>) {
      state.matchingInvoicesTotalPages = action.payload;
    },
    updateMatchingInvoicesLimit(state: VendorAccountUnderwritingState, action: PayloadAction<PaginationDropdownLimit>) {
      state.matchingInvoicesLimit = action.payload;
    },
    updateMatchingInvoicesSortField(state: VendorAccountUnderwritingState, action: PayloadAction<string>) {
      state.matchingInvoicesSortField = action.payload;
    },
    updateMatchingInvoicesSortDirection(state: VendorAccountUnderwritingState, action: PayloadAction<api.SortDirection>) {
      state.matchingInvoicesSortDirection = action.payload;
    },
    updateMatchingExternalInvoices(state: VendorAccountUnderwritingState, action: PayloadAction<boolean>) {
      state.matchingExternalInvoices = action.payload;
    },
    updateMatchingExternalInvoicesClientName(state: VendorAccountUnderwritingState, action: PayloadAction<string | undefined>) {
      state.matchingExternalInvoicesClientName = action.payload;
    },
    updateMatchingExternalInvoicesBeginningAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.matchingExternalInvoicesBeginningAt = action.payload;
    },
    updateMatchingExternalInvoicesEndingAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.matchingExternalInvoicesEndingAt = action.payload;
    },
    updateMatchingExternalInvoicesPage(state: VendorAccountUnderwritingState, action: PayloadAction<number>) {
      state.matchingExternalInvoicesPage = action.payload;
    },
    updateMatchingExternalInvoicesTotalPages(state: VendorAccountUnderwritingState, action: PayloadAction<number>) {
      state.matchingExternalInvoicesTotalPages = action.payload;
    },
    updateMatchingExternalInvoicesLimit(state: VendorAccountUnderwritingState, action: PayloadAction<PaginationDropdownLimit>) {
      state.matchingExternalInvoicesLimit = action.payload;
    },
    updateMatchingExternalInvoicesSortField(state: VendorAccountUnderwritingState, action: PayloadAction<string>) {
      state.matchingExternalInvoicesSortField = action.payload;
    },
    updateMatchingExternalInvoicesSortDirection(state: VendorAccountUnderwritingState, action: PayloadAction<api.SortDirection>) {
      state.matchingExternalInvoicesSortDirection = action.payload;
    },
    updateMatchingPaymentsMerchantName(state: VendorAccountUnderwritingState, action: PayloadAction<string | undefined>) {
      state.matchingPaymentsMerchantName = action.payload;
    },
    updateMatchingPaymentsBeginningAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.matchingPaymentsBeginningAt = action.payload;
    },
    updateMatchingPaymentsEndingAt(state: VendorAccountUnderwritingState, action: PayloadAction<api.ISODateString | undefined>) {
      state.matchingPaymentsEndingAt = action.payload;
    },
    updateMatchingPaymentsPage(state: VendorAccountUnderwritingState, action: PayloadAction<number>) {
      state.matchingPaymentsPage = action.payload;
    },
    updateMatchingPaymentsTotalPages(state: VendorAccountUnderwritingState, action: PayloadAction<number>) {
      state.matchingPaymentsTotalPages = action.payload;
    },
    updateMatchingPaymentsLimit(state: VendorAccountUnderwritingState, action: PayloadAction<PaginationDropdownLimit>) {
      state.matchingPaymentsLimit = action.payload;
    },
    updateMatchingPaymentsSortField(state: VendorAccountUnderwritingState, action: PayloadAction<string>) {
      state.matchingPaymentsSortField = action.payload;
    },
    updateMatchingPaymentsSortDirection(state: VendorAccountUnderwritingState, action: PayloadAction<api.SortDirection>) {
      state.matchingPaymentsSortDirection = action.payload;
    },
  },
});

export const {
  updateOverviewByVendorAccountIdBeginningAt,
  updateOverviewByVendorAccountIdEndingAt,
  updateOverviewByVendorAccountIdPeriodization,
  updateOverviewByVendorAccountIdMinimumBalance,
  updateTransactionsAmountLow,
  updateTransactionsAmountHigh,
  updateTransactionsMerchantName,
  updateTransactionsCategory,
  updateTransactionsAuthorizedBeginningAt,
  updateTransactionsAuthorizedEndingAt,
  updateTransactionsPostedBeginningAt,
  updateTransactionsPostedEndingAt,
  updateMatchingInvoicesClientName,
  updateMatchingInvoicesBeginningAt,
  updateMatchingInvoicesEndingAt,
  updateMatchingInvoicesPage,
  updateMatchingInvoicesTotalPages,
  updateMatchingInvoicesLimit,
  updateMatchingInvoicesSortField,
  updateMatchingInvoicesSortDirection,
  updateMatchingExternalInvoices,
  updateMatchingExternalInvoicesClientName,
  updateMatchingExternalInvoicesBeginningAt,
  updateMatchingExternalInvoicesEndingAt,
  updateMatchingExternalInvoicesPage,
  updateMatchingExternalInvoicesTotalPages,
  updateMatchingExternalInvoicesLimit,
  updateMatchingExternalInvoicesSortField,
  updateMatchingExternalInvoicesSortDirection,
  updateMatchingPaymentsMerchantName,
  updateMatchingPaymentsBeginningAt,
  updateMatchingPaymentsEndingAt,
  updateMatchingPaymentsPage,
  updateMatchingPaymentsTotalPages,
  updateMatchingPaymentsLimit,
  updateMatchingPaymentsSortField,
  updateMatchingPaymentsSortDirection,
} = vendorAccountUnderwritingSlice.actions;
export default vendorAccountUnderwritingSlice.reducer; 